import React, { useState, useMemo } from "react";
import Tooltip from "../ui/Tooltip";
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TaskFilter, List, Tag as TagType } from "../../types";
import { interpolatedOrderPosition } from "../../lib/orderPosition";
import { useDialog } from "../../contexts/DialogContext";
import { useView } from "../../contexts/ViewContext";
import BuiltInFilters from "./BuiltInFilters";
import FilterRowViewControls from "./FilterRowViewControls";

/* ------------------------------------------------------------------ */
/*  Sortable filter chip (unchanged)                                   */
/* ------------------------------------------------------------------ */

interface FilterRowProps {
  filters: TaskFilter[];
  onAddFilter: () => void;
  onEditFilter: (filter: TaskFilter) => void;
  onDeleteFilter: (filterId: string) => void;
  onReorderFilters: (filterId: string, newOrderPosition: number) => void;
  lists: List[];
  tags: TagType[];
  /** Lists with at least one open task — the lists popover hides the rest. */
  nonEmptyListIds: Set<string>;
}

interface SortableFilterChipProps {
  filter: TaskFilter;
  isActive: boolean;
  onFilterClick: (filterId: string) => void;
  onEditFilter: (filter: TaskFilter) => void;
  onDeleteFilter: (filterId: string) => void;
  lists: List[];
  tags: TagType[];
  chipIndex: number;
  totalChips: number;
}

const DATE_FILTER_LABELS: Record<string, string> = {
  today: "Today",
  planned: "Scheduled",
  "no-date": "Unscheduled",
  overdue: "Overdue",
  deadline: "With deadline",
};

const PRIORITY_LABEL_MAP: Record<number, string> = {
  1: "🔴 High",
  2: "🟠 Medium",
  3: "🟡 Low",
};

function buildFilterTooltip(
  filter: TaskFilter,
  lists: List[],
  tags: TagType[],
): string {
  const parts: string[] = [];

  if (filter.date_filters.length > 0) {
    const labels = filter.date_filters.map((d) => DATE_FILTER_LABELS[d] ?? d);
    parts.push(labels.join(", "));
  }

  if (filter.priorities.length > 0) {
    const labels = filter.priorities.map(
      (p) => PRIORITY_LABEL_MAP[p] ?? `P${p}`,
    );
    parts.push(labels.join(", ") + " priority");
  }

  if (filter.list_ids.length > 0) {
    const listNames = filter.list_ids
      .map((id) => lists.find((l) => l.id === id))
      .filter(Boolean)
      .map((l) => `${l!.emoji} ${l!.name}`);
    if (listNames.length > 0) parts.push(listNames.join(", "));
  }

  if (filter.tag_ids.length > 0) {
    const tagNames = filter.tag_ids
      .map((id) => tags.find((t) => t.id === id))
      .filter(Boolean)
      .map((tag) => tag!.name);
    if (tagNames.length > 0) {
      parts.push(
        tagNames.join(", ") + (tagNames.length === 1 ? " tag" : " tags"),
      );
    }
  }

  return parts.join(" · ");
}

const SortableFilterChip: React.FC<SortableFilterChipProps> = ({
  filter,
  isActive,
  onFilterClick,
  onEditFilter,
  onDeleteFilter,
  lists,
  tags,
  chipIndex,
  totalChips,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const tooltipText = useMemo(
    () => buildFilterTooltip(filter, lists, tags),
    [filter, lists, tags],
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: filter.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex items-center flex-shrink-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`flex-shrink-0 text-warm-text-tertiary cursor-grab active:cursor-grabbing transition-opacity duration-200 ease-out mr-1 ${
          isHovered ? "opacity-100" : "opacity-0"
        }`}
        aria-label={`Reorder ${filter.name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} />
      </div>

      <Tooltip
        content={tooltipText}
        placement="top"
        align="center"
        disabled={isDragging}
      >
        <button
          onClick={() => onFilterClick(filter.id)}
          aria-pressed={isActive}
          className={`
            px-3.5 py-1.5 text-sm font-semibold transition-all duration-150 whitespace-nowrap rounded-full
            ${
              isActive
                ? "bg-warm-blue text-white shadow-soft-sm scale-105"
                : "bg-warm-bg-elevated text-warm-text-secondary hover:bg-warm-border-subtle hover:scale-105"
            }
          `}
          {...(chipIndex === 0
            ? {
                "data-shortcut": "1",
                ...(totalChips > 1 ? { "data-shortcut-sublabel": "prev" } : {}),
              }
            : chipIndex === 1
              ? { "data-shortcut": "2", "data-shortcut-sublabel": "next" }
              : {})}
        >
          {filter.name}
        </button>
      </Tooltip>

      <div
        className={`absolute -top-1.5 -right-1.5 z-10 flex gap-0.5 transition-opacity duration-150 ease-in-out ${isHovered ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditFilter(filter);
          }}
          aria-label="Edit filter"
          className="p-1 rounded bg-warm-bg-elevated hover:bg-warm-blue hover:text-white text-warm-text-secondary transition-colors duration-150 ease-in-out shadow-soft-sm"
        >
          <Edit2 size={10} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeleteFilter(filter.id);
          }}
          aria-label="Delete filter"
          className="p-1 rounded bg-warm-bg-elevated hover:bg-red-500 hover:text-white text-warm-text-secondary transition-colors duration-150 ease-in-out shadow-soft-sm"
        >
          <Trash2 size={10} />
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  FilterRow                                                          */
/* ------------------------------------------------------------------ */

const FilterRow: React.FC<FilterRowProps> = ({
  filters,
  onAddFilter,
  onEditFilter,
  onDeleteFilter,
  onReorderFilters,
  lists,
  tags,
  nonEmptyListIds,
}) => {
  const {
    activeFilterId,
    setActiveFilterId,
    builtInFilterState,
    setBuiltInFilterState,
  } = useView();
  const dialog = useDialog();
  const shortcutHintsActive = dialog.isOpen("shortcut-hints");
  const [isRowHovered, setIsRowHovered] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const filterIds = useMemo(() => filters.map((f) => f.id), [filters]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const draggedFilter = filters.find((f) => f.id === active.id);
    const targetFilter = filters.find((f) => f.id === over.id);
    if (!draggedFilter || !targetFilter) return;

    const draggedIndex = filters.findIndex((f) => f.id === draggedFilter.id);
    const targetIndex = filters.findIndex((f) => f.id === targetFilter.id);

    onReorderFilters(
      draggedFilter.id,
      Math.floor(interpolatedOrderPosition(filters, draggedIndex, targetIndex)),
    );
  };

  const handleFilterClick = (filterId: string) => {
    setActiveFilterId(activeFilterId === filterId ? null : filterId);
  };

  return (
    <div
      className="sticky top-[65px] z-10 backdrop-blur-md border-b border-warm-border"
      style={{ backgroundColor: "var(--color-bg-card-translucent)" }}
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
    >
      <div className="px-4 sm:px-6 py-3 flex items-center overflow-x-auto">
        {/* Built-in filter buttons */}
        <BuiltInFilters
          builtInFilterState={builtInFilterState}
          onBuiltInFilterChange={setBuiltInFilterState}
          lists={lists}
          tags={tags}
          nonEmptyListIds={nonEmptyListIds}
          shortcutHintsActive={shortcutHintsActive}
          className="flex items-center gap-2"
        />

        {/* Divider: OOTB filters | custom filters */}
        <div className="w-px h-5 bg-warm-border flex-shrink-0 mx-3" />

        {/* Custom filter chips */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filterIds}
            strategy={horizontalListSortingStrategy}
          >
            {filters.map((filter, index) => (
              <SortableFilterChip
                key={filter.id}
                filter={filter}
                isActive={activeFilterId === filter.id}
                onFilterClick={handleFilterClick}
                onEditFilter={onEditFilter}
                onDeleteFilter={onDeleteFilter}
                lists={lists}
                tags={tags}
                chipIndex={index}
                totalChips={filters.length}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div className="relative flex-shrink-0 ml-3">
          <Tooltip
            content="Add custom filter"
            placement="top"
            align="center"
            disabled={!isRowHovered}
          >
            <button
              onClick={onAddFilter}
              aria-label="Add filter"
              className={`p-2 bg-warm-blue text-white hover:bg-warm-blue-dark rounded-full transition-all duration-150 shadow-soft-sm hover:shadow-soft-md active:scale-95 flex items-center ${
                isRowHovered ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              <Plus size={14} />
            </button>
          </Tooltip>
        </div>

        {/* Spacer */}
        <div className="flex-grow" />

        <FilterRowViewControls />
      </div>
    </div>
  );
};

export default FilterRow;
